import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const CalendarSelector = ({
    defaultMode = 'day',
    onDateSelect,
    onMonthSelect,
    onYearSelect,
    minDate = null,
    maxDate = null,
    disabledDates = [],
    className = '',
}) => {
    const [mode, setMode] = useState(defaultMode);
    const [currentDate, setCurrentDate] = useState(new Date());
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const [selectedDay, setSelectedDay] = useState(todayMidnight);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [showCustomPicker, setShowCustomPicker] = useState(false);
    const dropdownRef = useRef(null);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setShowCustomPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Helper to format date as dd/mm/yyyy
    const formatDate = (date) => {
        if (!date || isNaN(date.getTime())) return '';
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    // Check if date is disabled or in future
    const isFutureDate = (date) => date > today;
    const isDateDisabled = (date) => {
        if (minDate && date < minDate) return true;
        if (maxDate && date > maxDate) return true;
        if (isFutureDate(date)) return true;
        return disabledDates.some(d =>
            d.getDate() === date.getDate() &&
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear()
        );
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        return { daysInMonth, startingDayOfWeek, year, month };
    };

    const isDateInWeek = (date, weekStart) => {
        if (!weekStart) return false;
        const dayOfWeek = weekStart.getDay();
        const sunday = new Date(weekStart);
        sunday.setDate(weekStart.getDate() - dayOfWeek);
        sunday.setHours(0, 0, 0, 0);
        const week = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(sunday);
            d.setDate(sunday.getDate() + i);
            if (d > today) break;
            week.push(d);
        }
        return week.some(d =>
            d.getDate() === date.getDate() &&
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear()
        );
    };

    const handlePrevious = () => {
        const newDate = new Date(currentDate);
        if (mode === 'month' || mode === 'year') {
            newDate.setFullYear(newDate.getFullYear() - 1);
        } else {
            newDate.setMonth(newDate.getMonth() - 1);
        }
        setCurrentDate(newDate);
    };

    const handleNext = () => {
        const newDate = new Date(currentDate);

        if (mode === 'month' || mode === 'year') {
            newDate.setFullYear(newDate.getFullYear() + 1);
        } else {
            newDate.setMonth(newDate.getMonth() + 1);
        }

        setCurrentDate(newDate);
    };

    const handleDayClick = (day) => {
        const selected = new Date(currentDate.getFullYear(), currentDate.getMonth(), day, 0, 0, 0, 0);
        if (isDateDisabled(selected)) return;
        setSelectedDay(selected);
        setSelectedWeek(null);
        setSelectedMonth(null);
        setSelectedYear(null);
        if (onDateSelect) onDateSelect(formatDate(selected));
        setIsOpen(false);
    };

    const handleMonthClick = (monthIndex) => {
        const selected = new Date(currentDate.getFullYear(), monthIndex, 1);
        if (isFutureDate(selected)) return;
        setSelectedMonth(selected);
        setSelectedDay(null);
        setSelectedWeek(null);
        setSelectedYear(null);
        const toDate = (selected.getFullYear() === today.getFullYear() && selected.getMonth() === today.getMonth())
            ? today
            : new Date(selected.getFullYear(), selected.getMonth() + 1, 0);
        if (onMonthSelect) onMonthSelect({ startDate: formatDate(selected), endDate: formatDate(toDate) });
        setIsOpen(false);
    };

    const handleModeChange = (newMode) => {
        if (mode === newMode && isOpen) {
            setIsOpen(false);
            return;
        }
        setMode(newMode);
        setIsOpen(true);
        setShowCustomPicker(false);
    };



    const renderDayView = () => {
        const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
        const days = [];

        // Empty cells for days before the month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} style={{ height: '30px' }} />);
        }

        // Actual days
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const isDisabled = isDateDisabled(date);
            const isSelected = mode === 'day' && selectedDay?.getDate() === day && selectedDay?.getMonth() === month && selectedDay?.getFullYear() === year;
            const isInSelectedWeek = mode === 'week' && isDateInWeek(date, selectedWeek);

            days.push(
                <button
                    key={day}
                    onClick={() => !isDisabled && handleDayClick(day)}
                    disabled={isDisabled}
                    style={{
                        height: '30px',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        backgroundColor: isSelected || isInSelectedWeek ? '#2563eb' : 'transparent',
                        color: isSelected || isInSelectedWeek ? '#fff' : isDisabled ? '#d1d5db' : '#374151',
                        fontSize: '12px',
                        fontWeight: isSelected || isInSelectedWeek ? '600' : '400',
                        transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        if (!isDisabled && !isSelected && !isInSelectedWeek) {
                            e.target.style.backgroundColor = '#f3f4f6';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isDisabled && !isSelected && !isInSelectedWeek) {
                            e.target.style.backgroundColor = 'transparent';
                        }
                    }}
                >
                    {day}
                </button>
            );
        }

        return days;
    };
    const handleYearClick = (yearVal) => {
        if (yearVal > today.getFullYear()) return;
        setSelectedYear(yearVal);
        setSelectedDay(null);
        setSelectedWeek(null);
        setSelectedMonth(null);
        if (onYearSelect) onYearSelect({ startDate: `01/01/${yearVal}`, endDate: `31/12/${yearVal}` });
        setIsOpen(false);
    };

    const renderYearView = () => {
        const currentYear = currentDate.getFullYear();
        const startYear = currentYear - 6;

        return Array.from({ length: 12 }, (_, index) => {
            const yearValue = startYear + index;
            const isDisabled = yearValue > today.getFullYear();
            const isSelected = selectedYear === yearValue;

            return (
                <button
                    key={yearValue}
                    onClick={() => !isDisabled && handleYearClick(yearValue)}
                    disabled={isDisabled}
                    style={{
                        padding: "8px",
                        borderRadius: "6px",
                        border: isSelected
                            ? "1px solid #2563eb"
                            : "1px solid #e5e7eb",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        backgroundColor: isSelected
                            ? "#2563eb"
                            : "transparent",
                        color: isSelected
                            ? "#fff"
                            : isDisabled
                                ? "#d1d5db"
                                : "#374151",
                        fontWeight: isSelected ? "600" : "400",
                        fontSize: "12px",
                        transition: "all 0.2s"
                    }}
                >
                    {yearValue}
                </button>
            );
        });
    };
    const renderMonthView = () => {
        return months.map((month, index) => {
            const monthDate = new Date(currentDate.getFullYear(), index, 1);
            const isDisabled = isFutureDate(monthDate);
            const isSelected = mode === 'month' && selectedMonth?.getMonth() === index && selectedMonth?.getFullYear() === currentDate.getFullYear();

            return (
                <button
                    key={month}
                    onClick={() => !isDisabled && handleMonthClick(index)}
                    disabled={isDisabled}
                    style={{
                        padding: '8px',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        backgroundColor: isSelected ? '#2563eb' : 'transparent',
                        color: isSelected ? '#fff' : isDisabled ? '#d1d5db' : '#374151',
                        fontSize: '12px',
                        fontWeight: isSelected ? '600' : '400',
                        transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        if (!isDisabled && !isSelected) {
                            e.target.style.backgroundColor = '#f3f4f6';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isDisabled && !isSelected) {
                            e.target.style.backgroundColor = 'transparent';
                        }
                    }}
                >
                    {month}
                </button>
            );
        });
    };

    const modeButtonStyle = (isActive) => ({
        padding: '0 14px',
        height: '34px',
        boxSizing: 'border-box',
        borderRadius: '6px',
        fontWeight: '500',
        fontSize: '10.5px',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
        backgroundColor: isActive ? '#2563eb' : 'transparent',
        color: isActive ? '#fff' : '#374151'
    });

    const navButtonStyle = {
        padding: '6px',    // was 8px
        borderRadius: '6px',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    return (
        <div ref={dropdownRef} style={{ position: 'relative', width: 'fit-content' }} className={className}>
            <div style={{ display: 'flex', gap: '2px', alignItems: 'center', backgroundColor: '#e5e7eb', padding: '4px', borderRadius: '8px', height: '40px', boxSizing: 'border-box' }}>
                <button
                    onClick={() => handleModeChange('day')}
                    style={modeButtonStyle(mode === 'day')}
                >
                    Day
                </button>

                <button
                    onClick={() => handleModeChange('month')}
                    style={modeButtonStyle(mode === 'month')}
                >
                    Month
                </button>

                <button
                    onClick={() => handleModeChange('year')}
                    style={modeButtonStyle(mode === 'year')}
                >
                    Year
                </button>

            </div>

            {isOpen && !showCustomPicker && (
                <div style={{
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    padding: '16px',
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    zIndex: 1001,
                    minWidth: '240px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <button onClick={handlePrevious} style={navButtonStyle}>
                            <ChevronLeft size={16} />
                        </button>
                        <h2 style={{ fontSize: '11px', fontWeight: '600', margin: 0 }}>
                            {mode === 'month' ? currentDate.getFullYear() : `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
                        </h2>
                        <button onClick={handleNext} style={navButtonStyle}>
                            <ChevronRight size={16} />
                        </button>
                    </div>

                    {mode !== 'month' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '5px' }}>
                            {daysOfWeek.map(day => (
                                <div key={day} style={{ textAlign: 'center', fontSize: '9px', fontWeight: '600', color: '#6b7280', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {day}
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns:
                            mode === 'month' || mode === 'year'
                                ? 'repeat(3, 1fr)'
                                : 'repeat(7, 1fr)',
                        gap: '2px'
                    }}>
                        {mode === 'month'
                            ? renderMonthView()
                            : mode === 'year'
                                ? renderYearView()
                                : renderDayView()}
                    </div>

                    {/* {showSelection && ( */}

                    {/* )} */}
                </div>
            )}


        </div>
    );
};